import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Modal } from '@syneto/compass-react';
import { getApiErrorMessage } from '../../../../helpers/apiError';
import { moveProduct } from '../../../../services/products';
import { describeJobOutcome, isJobSucceeded, pollJobUntilTerminal } from '../../../../services/jobs';

/*
 * Asks for the new VM's serial and moves the product to it: the PATCH answers with a job, and the product only has
 * the new serial once that job succeeded. A refused serial or a failed job is shown here and the dialog stays open,
 * so nothing changed and the operator can try another serial or cancel (SYN-3453).
 */
export const MoveProductModal = ({ show, product, onHide, onMoved }) => {
	const [serialNumber, setSerialNumber] = useState('');
	const [error, setError] = useState(null);
	const [isMoving, setIsMoving] = useState(false);
	const pollController = useRef(null);

	useEffect(() => {
		if (show) {
			setSerialNumber('');
			setError(null);
		}
	}, [show]);

	useEffect(() => {
		return () => {
			pollController.current?.abort();
		};
	}, []);

	const newSerial = serialNumber.trim();

	const move = async () => {
		if (!newSerial || isMoving) {
			return;
		}

		setIsMoving(true);
		setError(null);
		pollController.current = new AbortController();

		try {
			const response = await moveProduct(product.id, newSerial);
			const job = await pollJobUntilTerminal(response.data.jobId, { signal: pollController.current.signal });

			if (!isJobSucceeded(job)) {
				setError(describeJobOutcome(job, { failureMessage: 'The product could not be moved', unappliedMessage: 'The product was not moved' }));
				setIsMoving(false);
				return;
			}

			setIsMoving(false);
			onMoved(newSerial);
		} catch (moveError) {
			if (moveError?.name === 'AbortError') {
				return;
			}
			setError(getApiErrorMessage(moveError));
			setIsMoving(false);
		}
	};

	const content = (
		<div>
			{error && <Alert appearance="danger">{error}</Alert>}
			<p>
				The key, licenses and maintenance of this product move to the VM with the serial below.
				{product?.serialNumber && <> It is on <strong>{product.serialNumber}</strong> now.</>}
			</p>
			<Alert appearance="warning">
				The old machine can no longer sync with Central. It keeps its last licenses until someone unlinks it.
			</Alert>
			<label className="form-label" htmlFor="move-product-serial">New VM&apos;s serial number*</label>
			<input
				id="move-product-serial"
				type="text"
				className="form-control"
				autoComplete="off"
				value={serialNumber}
				onChange={(event) => { setSerialNumber(event.target.value); }}
				onKeyDown={(event) => {
					if (event.key === 'Enter') {
						event.preventDefault();
						move();
					}
				}}
				disabled={isMoving}
			/>
		</div>
	);

	return (
		<Modal
			enforceFocus={false}
			show={show}
			onHide={isMoving ? () => {} : onHide}
			title="Move to another machine"
			content={content}
			actions={{
				primary: {
					label: isMoving ? 'Moving…' : 'Move',
					onClick: move,
					disabled: isMoving || !newSerial
				},
				secondary: {
					label: 'Cancel',
					onClick: onHide,
					disabled: isMoving
				}
			}}
		/>
	);
};

MoveProductModal.propTypes = {
	show: PropTypes.bool.isRequired,
	// The product being moved: its `id`, and its current `serialNumber` when it has one.
	product: PropTypes.object,
	onHide: PropTypes.func.isRequired,
	// Called with the new serial once the move's job succeeded; the caller loads the product again.
	onMoved: PropTypes.func.isRequired
};

export default MoveProductModal;
